import { useCallback, useRef, useState } from "react";
import {
  LiveAvatarSession,
  SessionEvent,
  AgentEventsEnum,
} from "@heygen/liveavatar-web-sdk";
import fetchHeygenAccessToken from "@/services/heygen/fetchHeygenAccessToken";
import toaster from "@/lib/toaster";
import { ChatStore } from "@/stores/ChatStore";
import normalizeMessage from "@/lib/normalizeMessage";
import useIdleTimeout from "./useIdleTimeout";
import { v4 } from "uuid";
import SettingsStore from "@/stores/SettingsStore";
import saveSessionId from "@/services/queries/saveSessionId";
import type { TAvatar } from "@/types/TAvatar";

export default function useAvatarSession() {
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isStreamReady, setIsStreamReady] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<TAvatar | undefined>();
  const session = useRef<LiveAvatarSession | null>(null);
  const isRunning = ChatStore(state => state.isRunning);
  const resetDeltaBuffer = ChatStore(state => state.resetDeltaBuffer);
  const appendDeltaBuffer = ChatStore(state => state.appendDeltaBuffer);
  const addMessage = ChatStore(state => state.addMessage);
  const setAvatarStatus = ChatStore(state => state.setAvatarStatus);
  const setIsRunning = ChatStore(state => state.setIsRunning);
  const setIsAvatarReady = ChatStore(state => state.setIsAvatarReady);
  const setIsAvatarSpeaking = ChatStore(state => state.setIsAvatarSpeaking);
  const avatars = SettingsStore(state => state.avatars);
  const welcomeMessage = SettingsStore(state => state.welcomeMessage);

  const selectRandomAvatar = useCallback(() => {
    const weightedAvatars = avatars.filter(a => (a.weight ?? 1.0) > 0);
    if (weightedAvatars.length === 0) return avatars[0];

    const totalWeight = weightedAvatars.reduce((sum, a) => sum + (a.weight ?? 1.0), 0);
    let random = Math.random() * totalWeight;

    for (const avatar of weightedAvatars) {
      random -= (avatar.weight ?? 1.0);
      if (random <= 0) return avatar;
    }

    return weightedAvatars[0];
  }, [avatars]);

  const startSession = useCallback(async () => {
    setIsLoadingSession(true);

    const newAvatar = selectRandomAvatar();
    setSelectedAvatar(newAvatar);

    const tokenResponse = await fetchHeygenAccessToken();
    if (!tokenResponse.session_token) {
      console.error("Failed to get session token");
      setIsRunning(false);
      setIsLoadingSession(false);
      return;
    }

    session.current = new LiveAvatarSession(tokenResponse.session_token, {
      voiceChat: false,
    });

    session.current.on(SessionEvent.SESSION_STREAM_READY, () => {
      if (!isRunning) {
        console.warn("Avatar session started, but isRunning is false. Stopping session.");
        endSession();
        return;
      }

      saveSessionId(tokenResponse.session_id);
      setIsStreamReady(true);
      setAvatarStatus("Bereit");
      setIsAvatarReady(true);
      toaster("Avatar Stream ist bereit");
      if (welcomeMessage) {
        addMessage({
          transcript: welcomeMessage,
          sender: "assistant",
          id: v4(),
          timestamp: new Date(),
        });
        setTimeout(() => {
          session.current?.repeat(normalizeMessage(welcomeMessage));
        }, 500);
      }
    });

    session.current.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
      setIsAvatarSpeaking(true);
      setAvatarStatus("Sprechen");
    });

    session.current.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
      setIsAvatarSpeaking(false);
      setAvatarStatus("Bereit");
    });

    try {
      await session.current.start();
    } catch (error) {
      console.error("Error starting avatar session:", error);
      setIsRunning(false);
    } finally {
      setIsLoadingSession(false);
    }
  }, [
    selectRandomAvatar,
    welcomeMessage,
    isRunning,
    appendDeltaBuffer,
    setAvatarStatus,
    setIsAvatarReady,
    setIsRunning,
    setIsAvatarSpeaking,
    addMessage,
  ]);

  const endSession = useCallback(async () => {
    try {
      await session.current?.stop();
    } catch (error) {
      console.error("Error stopping avatar session:", error);
    }
    session.current = null;
    setIsStreamReady(false);
  }, []);

  const { resetIdleTimer } = useIdleTimeout({ callback: endSession });

  const handleSpeak = useCallback(
    async (message?: string) => {
      if (!message) return;
      const normalizedMessage = normalizeMessage(message);
      resetIdleTimer();
      try {
        session.current?.repeat(normalizedMessage);
      } catch (error) {
        console.error("Error getting response:", error);
      }
    },
    [resetIdleTimer],
  );

  const interruptAvatar = useCallback(() => {
    console.log("interrupt()");
    session.current?.interrupt();
    resetDeltaBuffer();
  }, [resetDeltaBuffer]);

  const attachVideo = useCallback((element: HTMLMediaElement | null) => {
    if (element && session.current) {
      session.current.attach(element);
    }
  }, []);

  return {
    isStreamReady,
    isLoadingSession,
    startSession,
    endSession,
    handleSpeak,
    interruptAvatar,
    selectedAvatar,
    attachVideo,
  };
}
